import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderOpen, Plus, Trash2, Calendar, MapPin, Clock, Film } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDuration } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import NewProjectDialog from "@/components/NewProjectDialog";

export default function Projects() {
  const { projects, refreshProjects, selectProject, projectId } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openProject = (id) => { selectProject(id); navigate("/"); };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its media/jobs? Storage copies remain.")) return;
    try {
      await api.deleteProject(id, true);
      if (projectId === id) selectProject(null);
      await refreshProjects();
      toast.success("Project deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 fade-up">
      <NewProjectDialog open={open} onClose={() => setOpen(false)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Projects</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Your wedding post-production projects</p>
        </div>
        <button data-testid="btn-new-project-page" className="btn btn-gold" onClick={() => setOpen(true)}>
          <Plus size={16} /> New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="studio-card p-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#212534] flex items-center justify-center mb-4">
            <FolderOpen size={30} className="text-[#E6B04E]" />
          </div>
          <h3 className="font-head text-xl font-bold mb-1">No projects yet</h3>
          <p className="text-[#94A3B8] text-sm mb-5 max-w-sm">Create a wedding project, then import raw footage to begin scanning, proxy generation and analysis.</p>
          <button className="btn btn-gold" onClick={() => setOpen(true)}><Plus size={16} /> Create your first project</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => openProject(p.id)}
              className="studio-card p-5 cursor-pointer hover:border-[#3B4259] transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E6B04E]/20 to-transparent border border-[#E6B04E]/30 flex items-center justify-center">
                  <Film size={20} className="text-[#E6B04E]" />
                </div>
                <button data-testid={`delete-project-${p.id}`} onClick={(e) => remove(e, p.id)}
                  className="opacity-0 group-hover:opacity-100 text-[#64748B] hover:text-[#EF4444] transition-all p-1.5">
                  <Trash2 size={16} />
                </button>
              </div>
              <h3 className="font-head font-bold text-lg mb-0.5 truncate">{p.name}</h3>
              <p className="text-[#94A3B8] text-sm mb-3">{p.bride_name || "—"} &amp; {p.groom_name || "—"}</p>
              <div className="space-y-1.5 text-[12px] text-[#64748B] font-mono">
                {p.venue && <div className="flex items-center gap-2"><MapPin size={12} /> {p.venue}{p.city ? `, ${p.city}` : ""}</div>}
                {p.wedding_date && <div className="flex items-center gap-2"><Calendar size={12} /> {p.wedding_date}</div>}
                <div className="flex items-center gap-2"><Clock size={12} /> Target {fmtDuration(p.target_duration_min * 60)} · {p.wedding_type}</div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#252A3B] flex items-center justify-between">
                <span className="tech-badge">{p.clip_count} CLIPS</span>
                <span className="text-[11px] font-mono text-[#E6B04E]">Open →</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
